<template>
  <!-- Use general search from parent component -->
  <!-- Lobby Principals -->
  <div class="card">
    <div class="card-content white" v-if="quote && !isSubmitting && status === 'a'">
      <div class="lobby-readiness" :class="{ 'lobby-readiness-ok': canProceed }">
        <i class="material-icons">{{ canProceed ? "verified_user" : "report_problem" }}</i>
        <span>{{ readinessMessage }}</span>
      </div>

      <!-- HMO, Retail -->
      <button
        class="waves-effect waves-light btn-large teal darken-4 right"
        :class="{
          disabled: !canProceed || changeToNairaTotal(quote.total) == 0 || isSubmitting,
        }"
        @click="handleConfirmation()"
        v-if="cat != 'tpa'"
      >
        <i class="medium material-icons right">check_circle</i>Generate Invoice
        (NGN {{ changeToNairaTotal(quote.total) }})
      </button>

      <!-- TPA -->
      <button
        class="waves-effect waves-light btn-large teal darken-4 right"
        :class="{
          disabled: !canProceed || !enrollees.length || isSubmitting,
        }"
        @click="handleActivateTPA()"
        v-else
      >
        <i class="medium material-icons right">check_circle</i>Activate TPA
      </button>
    </div>

    <div class="card" :class="{ 'teal lighten-5': !isSubmitting }">
      <div class="progress" v-if="isSubmitting">
        <div class="indeterminate"></div>
      </div>

      <div class="card-content red lighten-5 red-text text-darken-3" v-if="error">
        {{ error }}
      </div>

      <br v-if="!isSubmitting" />

      <!-- Link after submission -->
      <template v-if="cat != 'retail' && showLink">
        <div class="card-content spacer10">
          <div class="row amber lighten-5">
            <div class="col s12 spacer10">
              <label class="active teal-text">Registration</label>
              <h6><b>General Link</b></h6>
              <p>
                {{
                  `${applicationURL}/utility/view/${clientId}/${Date.now()}`
                }}
              </p>
              <br />
            </div>
          </div>
        </div>
      </template>

      <div class="card-content white">
        <table class="highlight responsive-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Name <small>(Enrollee ID)</small></th>
              <th>Plan</th>
              <th>Type</th>
              <th>Billable</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody v-for="(enrollee, index) in enrollees" :key="enrollee.id">
            <tr>
              <td>{{ index + 1 }}</td>
              <td>
                {{ enrollee.name }}<br />
                <span v-if="enrollee.enrollee_number">({{ enrollee.enrollee_number }})</span>
              </td>
              <td>{{ enrollee.plan.name }}</td>
              <td>{{ enrollee.type == "f" ? "Family" : "Individual" }}</td>
              <td>
                <span>NGN {{ changeToNaira(enrollee.plan.premiums, enrollee.type) }}</span>
              </td>
              <td>
                <span
                  class="new badge green"
                  data-badge-caption="Ready"
                  v-if="!principalIssues(enrollee.id).length"
                ></span>
                <span
                  class="new badge orange darken-2"
                  data-badge-caption="Needs attention"
                  v-else
                ></span>
              </td>
              <td class="lobby-actions">
                <a href="#" @click.prevent="startPlanEdit(enrollee)">Change Plan</a>
                <a href="#" class="red-text text-darken-2" @click.prevent="startFamilyDeactivation(enrollee)">De-activate Family</a>
              </td>
            </tr>

            <tr v-if="confirmingPrincipalId == enrollee.id">
              <td colspan="7">
                <div class="confirm-strip">
                  <div>
                    <strong>De-activate {{ enrollee.name }} and family?</strong>
                    <p>
                      This will move the pending principal, spouse, and dependants out of this lobby batch and into the <strong>Bin</strong> before invoices are generated.
                    </p>
                  </div>
                  <button
                    class="btn-small red darken-2"
                    :class="{ disabled: isSubmitting }"
                    @click.prevent="removePrincipal(enrollee)"
                  >
                    De-activate Family
                  </button>
                  <a href="#" class="btn-flat" @click.prevent="cancelFamilyDeactivation">Cancel</a>
                </div>
              </td>
            </tr>

            <tr v-if="editingPrincipalId == enrollee.id">
              <td colspan="7">
                <div class="plan-edit-strip">
                  <div class="input-field">
                    <label class="active teal-text" :for="`lobby-plan-${enrollee.id}`">Plan</label>
                    <select :id="`lobby-plan-${enrollee.id}`" v-model="editPlanId" class="validate">
                      <option v-for="plan in planOptions" :key="plan.id" :value="plan.id">
                        {{ plan.name }}
                      </option>
                    </select>
                  </div>

                  <div class="input-field">
                    <label class="active teal-text" :for="`lobby-type-${enrollee.id}`">Type</label>
                    <select :id="`lobby-type-${enrollee.id}`" v-model="editType" class="validate">
                      <option value="i" v-if="selectedPlanHasType('i')">Individual</option>
                      <option value="f" v-if="selectedPlanHasType('f')">Family</option>
                    </select>
                  </div>

                  <button
                    class="btn-small teal darken-4"
                    :class="{ disabled: !editPlanId || !editType || isSubmitting }"
                    @click.prevent="updatePlan(enrollee)"
                  >
                    Apply
                  </button>

                  <a href="#" class="btn-flat" @click.prevent="cancelPlanEdit">Cancel</a>
                </div>

                <p class="grey-text text-darken-1" v-if="editType == 'i'">
                  Switching to individual will administratively remove spouse and dependants from this lobby record.
                </p>
              </td>
            </tr>

            <tr v-if="principalIssues(enrollee.id).length || activeFamily(enrollee).length">
              <td colspan="7">
                <div class="issue-panel" v-if="principalIssues(enrollee.id).length">
                  <div
                    class="issue-line"
                    v-for="issue in principalIssues(enrollee.id)"
                    :key="`${issue.code}-${issue.member_type}-${issue.member_id}`"
                  >
                    <i class="material-icons">error_outline</i>
                    <span>{{ issue.message }}</span>
                    <a
                      href="#"
                      class="red-text text-darken-2"
                      v-if="issue.member_type == 'spouse'"
                      @click.prevent="removeSpouse(enrollee, issue.member_id)"
                    >Remove spouse</a>
                    <a
                      href="#"
                      class="red-text text-darken-2"
                      v-if="issue.member_type == 'dependant'"
                      @click.prevent="removeDependant(enrollee, issue.member_id)"
                    >Remove dependant</a>
                  </div>
                </div>

                <div class="family-strip" v-if="activeFamily(enrollee).length">
                  <span class="family-chip" v-for="member in activeFamily(enrollee)" :key="`${member.type}-${member.id}`">
                    {{ member.label }}: {{ member.name }}
                    <a href="#" @click.prevent="removeFamilyMember(enrollee, member)">remove</a>
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, onUpdated, ref } from "vue";
import { ageSex, changeToMoney } from "@/composables/fxLibrary/coreFx";
import { appURL } from "@/laravel/config";
import getItemsOfItems from "@/composables/getItemsOfItems";
import postItem from "@/composables/postItem";
import M from "materialize-css";

export default {
  name: "PrincipalLobbyClientComponent",
  props: ["status", "cat", "clientId", "enrollees", "quote", "policy"],
  emits: ["requote"],
  setup(props, context) {
    const isSubmitting = ref(false);
    const isLoading = ref(false);
    const showLink = ref(false);
    const error = ref(null);
    const plansResponse = ref(null);
    const editingPrincipalId = ref(null);
    const confirmingPrincipalId = ref(null);
    const editPlanId = ref(null);
    const editType = ref(null);

    const initSelects = () => {
      const elemsSelect = document.querySelectorAll("select");
      M.FormSelect.init(elemsSelect, {});
    };

    const getPlans = () => {
      (async () => {
        const { res, err } = await getItemsOfItems("plan/client/plans", props.clientId);

        if (res.value) {
          plansResponse.value = res.value.data;
        } else {
          error.value = err.value;
        }
      })();
    };

    onMounted(() => {
      initSelects();
      getPlans();
    });

    onUpdated(() => {
      initSelects();
    });

    // ageSex
    const showAgeSex = (dob, sex) => {
      const { fx } = ageSex(dob, sex);
      return fx.value;
    };

    // to naira
    const changeToNaira = (premiums, type) => {
      let val;
      premiums.forEach((x) => {
        if (x.type == type && !val) {
          val = x.amount;
        }
      });
      const { fx } = changeToMoney(val);
      return fx.value;
    };

    // to naira
    const changeToNairaTotal = (val) => {
      const { fx } = changeToMoney(val);
      return fx.value;
    };

    const readiness = computed(() => props.quote?.readiness || null);

    const readinessIssues = computed(() => {
      return Array.isArray(readiness.value?.issues) ? readiness.value.issues : [];
    });

    const checksComplete = computed(() => !!props.quote && !!readiness.value);

    const canProceed = computed(() => {
      return checksComplete.value && readiness.value?.ok === true;
    });

    const readinessMessage = computed(() => {
      if (!checksComplete.value) {
        return "Checking lobby records before invoice generation...";
      }

      if (canProceed.value) {
        return "All lobby checks passed. This batch is ready for invoicing.";
      }

      return `${readinessIssues.value.length} issue(s) must be resolved before invoice generation.`;
    });

    const principalIssues = (principalId) => {
      return readinessIssues.value.filter((issue) => issue.principal_id == principalId);
    };

    const planOptions = computed(() => {
      const plans = Array.isArray(plansResponse.value) ? plansResponse.value : [];

      return plans.filter((plan) => {
        if (!Array.isArray(plan.premiums)) return false;

        if (props.cat == "tpa") {
          return plan.premiums.some((premium) => premium.type == "i" && premium.amount == 0);
        }

        return plan.premiums.some((premium) => premium.type == "i" && premium.amount > 0);
      });
    });

    const selectedPlan = computed(() => {
      return planOptions.value.find((plan) => plan.id == editPlanId.value) || null;
    });

    const selectedPlanHasType = (type) => {
      return !!selectedPlan.value?.premiums?.find((premium) => {
        if (premium.type != type) return false;
        return props.cat == "tpa" ? premium.amount == 0 : premium.amount > 0;
      });
    };

    const activeFamily = (principal) => {
      const family = [];

      if (principal.spouse && principal.spouse.status != "d") {
        family.push({
          id: principal.spouse.id,
          type: "spouse",
          label: "Spouse",
          name: principal.spouse.name,
        });
      }

      (principal.dependants || [])
        .filter((dependant) => dependant.status != "d")
        .forEach((dependant) => {
          family.push({
            id: dependant.id,
            type: "dependant",
            label: "Dependant",
            name: dependant.name,
          });
        });

      return family;
    };

    const startPlanEdit = (principal) => {
      confirmingPrincipalId.value = null;
      editingPrincipalId.value = principal.id;
      editPlanId.value = principal.plan_id;
      editType.value = principal.type;
    };

    const cancelPlanEdit = () => {
      editingPrincipalId.value = null;
      editPlanId.value = null;
      editType.value = null;
    };

    const startFamilyDeactivation = (principal) => {
      cancelPlanEdit();
      confirmingPrincipalId.value = principal.id;
    };

    const cancelFamilyDeactivation = () => {
      confirmingPrincipalId.value = null;
    };

    const postLobbyAction = async (endpoint, payload) => {
      isSubmitting.value = true;
      error.value = null;

      const { res, err } = await postItem(endpoint, payload);

      if (res.value) {
        cancelPlanEdit();
        cancelFamilyDeactivation();
        handleReQuote();
      } else {
        error.value = err.value || "Action failed. Please review and try again.";
      }

      isSubmitting.value = false;
    };

    const updatePlan = (principal) => {
      postLobbyAction("operator/client/lobby/principal/plan/update", {
        client_id: props.clientId,
        principal_id: principal.id,
        plan_id: editPlanId.value,
        type: editType.value,
      });
    };

    const removePrincipal = (principal) => {
      postLobbyAction("operator/client/lobby/principal/remove", {
        client_id: props.clientId,
        principal_id: principal.id,
      });
    };

    const removeSpouse = (principal, spouseId) => {
      postLobbyAction("operator/client/lobby/spouse/remove", {
        client_id: props.clientId,
        principal_id: principal.id,
        spouse_id: spouseId,
      });
    };

    const removeDependant = (principal, dependantId) => {
      postLobbyAction("operator/client/lobby/dependant/remove", {
        client_id: props.clientId,
        principal_id: principal.id,
        dependant_id: dependantId,
      });
    };

    const removeFamilyMember = (principal, member) => {
      if (member.type == "spouse") {
        removeSpouse(principal, member.id);
      } else {
        removeDependant(principal, member.id);
      }
    };

    // handle re-activation here
    const handleConfirmation = async () => {
      if (!canProceed.value) return;

      isSubmitting.value = true;

      const newVal = {
        client_id: props.clientId,
        client_cat: props.cat,
        policy_id: props.policy?.id || props.quote?.policy?.id,
      };

      const { res, err } = await postItem(
        "operator/client/invoice/create",
        newVal
      );

      if (res.value) {
        handleReQuote();
        showLink.value = true;
      } else {
        error.value = err.value;
      }

      isSubmitting.value = false;
    };

    // handle re-activation here
    const handleActivateTPA = async () => {
      if (!canProceed.value) return;

      isSubmitting.value = true;

      const newVal = {
        client_id: props.clientId,
        client_cat: props.cat,
        policy_id: props.policy?.id || props.quote?.policy?.id,
      };

      const { res, err } = await postItem(
        "operator/client/invoice/create/activate/tpa",
        newVal
      );

      if (res.value) {
        handleReQuote();
        showLink.value = true;
      } else {
        error.value = err.value;
      }

      isSubmitting.value = false;
    };

    // handle
    const handleReQuote = () => {
      context.emit("requote", true);
    };

    return {
      handleReQuote,
      handleConfirmation,
      handleActivateTPA,
      startPlanEdit,
      cancelPlanEdit,
      updatePlan,
      removePrincipal,
      startFamilyDeactivation,
      cancelFamilyDeactivation,
      removeSpouse,
      removeDependant,
      removeFamilyMember,
      changeToNaira,
      changeToNairaTotal,
      showAgeSex,
      readinessMessage,
      canProceed,
      checksComplete,
      principalIssues,
      activeFamily,
      planOptions,
      selectedPlanHasType,
      editingPrincipalId,
      confirmingPrincipalId,
      editPlanId,
      editType,
      showLink,
      isSubmitting,
      isLoading,
      error,
      applicationURL: appURL,
    };
  },
};
</script>

<style scoped>
.progress {
  margin-top: 0px;
}

.spacer10 {
  margin-top: 10px;
  margin-bottom: 10px;
  margin-right: 10px;
  margin-left: 10px;
}

.lobby-readiness {
  align-items: center;
  background: #fff3e0;
  border-left: 4px solid #ef6c00;
  border-radius: 4px;
  color: #4e342e;
  display: inline-flex;
  gap: 10px;
  margin-bottom: 16px;
  margin-right: 16px;
  padding: 10px 12px;
}

.lobby-readiness i {
  color: #ef6c00;
  font-size: 20px;
}

.lobby-readiness-ok {
  background: #e8f5e9;
  border-left-color: #2e7d32;
  color: #1b5e20;
}

.lobby-readiness-ok i {
  color: #2e7d32;
}

.lobby-actions {
  min-width: 180px;
}

.lobby-actions a {
  display: block;
  font-weight: 600;
  margin-bottom: 6px;
}

.plan-edit-strip {
  align-items: center;
  background: #fafafa;
  border: 1px solid #eeeeee;
  border-radius: 4px;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(180px, 1fr) minmax(140px, 180px) auto auto;
  padding: 14px;
}

.confirm-strip {
  align-items: center;
  background: #ffebee;
  border: 1px solid #ffcdd2;
  border-radius: 4px;
  display: grid;
  gap: 14px;
  grid-template-columns: 1fr auto auto;
  padding: 14px;
}

.confirm-strip p {
  color: #5d4037;
  margin: 5px 0 0;
}

.issue-panel {
  background: #fff8e1;
  border-left: 4px solid #f9a825;
  border-radius: 4px;
  margin-bottom: 10px;
  padding: 10px 12px;
}

.issue-line {
  align-items: center;
  color: #4e342e;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 5px 0;
}

.issue-line i {
  color: #ef6c00;
  font-size: 19px;
}

.issue-line a {
  font-weight: 600;
}

.family-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.family-chip {
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 14px;
  color: #424242;
  display: inline-flex;
  gap: 8px;
  padding: 5px 10px;
}

.family-chip a {
  font-weight: 600;
}

.pointer {
  cursor: default;
}

.edit-item:hover .material-icons.grey-text,
.edit-item:hover .material-icons.grey-text::before {
  color: rgb(21, 21, 206) !important;
}

/* open */
.chip-more {
  cursor: pointer;
  padding-left: 2px;
  padding-right: 2px;
  padding-top: 2px;
  padding-bottom: 2px;
}

.chip-more:hover {
  font-size: 150%;
}

/* close */
.chip-less {
  cursor: pointer;
  color: white !important;
  background-color: black !important;
}

.chip-less:hover {
  color: black !important;
  background-color: #eaecee !important;
}

/* base class */
.tab-custom {
  display: inline-block;
  cursor: pointer;
  margin: 0 0 -1px;
  padding: 10px 15px;
  font-weight: 600;
  text-align: center;
  border: 1px solid transparent;
}

.tab-custom:hover {
  color: #000000 !important;
}

.tab-custom-active {
  display: inline-block;
  cursor: pointer;
  margin: 0 0 -1px;
  padding: 10px 15px;
  color: white;
  font-weight: 600;
  text-align: center;
  background-color: black;
}

/* display */
.tab-custom-section {
  padding: 20px 0 0;
  border-top: 1px solid #ddd;
}

@media (max-width: 700px) {
  .plan-edit-strip,
  .confirm-strip {
    grid-template-columns: 1fr;
  }
}
</style>
